<template>
  <Loader v-if="isLoading" />

  <div v-else class="all-photos-with-grouping">
    <div v-if="isDebug" class="debug-text" :style="debugStyles">{{ debugText }}</div>
    <TabsSection v-if="!onlyShowCustomerUploads" class="all-photos-with-grouping__tabs" :groupingType="groupingType" @setGroupingType="setGroupingType" />

    <SearchAndFilters v-if="!onlyShowCustomerUploads" class="all-photos-with-grouping__search-and-filters" :groupingType="groupingType" :customerId="customerId">
      <template #searchInput>
        <input class="global-text-input global-text-input--search" type="text" v-model="filterText" @input="searchChanged" @keyup.enter="applyFilters()" placeholder="" />
      </template>

      <template #photosCheckbox>
        <input type="checkbox" name="photos" v-model="filterShowPhotos" @change="searchChanged" />
      </template>
      <template #videosCheckbox>
        <input type="checkbox" name="videos" v-model="filterShowVideos" @change="searchChanged" />
      </template>
    </SearchAndFilters>

    <!-- Counter and sorting -->
    <div class="counter-and-sorting all-photos-with-grouping__counter-and-sorting">
      <div class="counter-and-sorting__counter-and-reorder-button">
        <div
          v-if="showTotalAssetCount"
          class="counter-and-sorting__counter"
          :style="{
            visibility: filterPhotoCount > 0 && showTotalAssetCount ? 'visible' : 'hidden',
          }"
        >
          <b>{{ filterPhotoCount.toLocaleString() }}</b> asset{{ filterPhotoCount === 1 ? "" : "s" }} from <b>{{ groupResultCount.toLocaleString() }}</b> {{ groupTypeTextPluralized }}
        </div>

        <LinkWithIcon
          v-if="groupingType === 'my-uploads' && dataFromServer?.customerGalleryPhotoGroups?.length"
          class="counter-and-sorting__reorder-button"
          isDottedUnderline
          color="teal"
          @click="isDeleteOrMoveMode = !isDeleteOrMoveMode"
        >
          <template #icon>
            <IconEmbedded v-if="isDeleteOrMoveMode" name="check_4" :size="15" />
            <IconEmbedded v-else name="pencil_2-5" :size="16" />
          </template>
          <span v-if="isDeleteOrMoveMode">Done Moving/Deleting</span>
          <span v-else>Move/Delete Photos</span>
        </LinkWithIcon>
      </div>

      <!-- Sorting selector -->
      <div v-if="dataFromServer?.customerGalleryPhotoGroups?.length" class="sorting-selector counter-and-sorting__sorting-selector">
        <!-- <div class="global-h6 sorting-selector__title">Sorting:</div> -->

        <LinkWithIcon class="sorting-selector__selector" @click="reverseSort()" isDottedUnderline iconInset="2px auto auto 2px" color="gray">
          <template #icon><IconEmbedded :name="sortDescending ? 'descending_3' : 'ascending_3'" :size="15" /></template>
          {{ sortFieldText }} first
        </LinkWithIcon>
      </div>
      <!-- / Sorting selector -->
    </div>
    <!-- / Counter and sorting -->

    <NoteWithIcon
      v-if="showCommunityUploadLinksNote && groupingType === 'community-upload' && dataFromServer?.communityUploadPhotoSummaries?.length"
      class="all-photos-with-grouping__community-upload-links-note"
      color="blue"
      size="small"
    >
      <template #text>
        For Community Upload public links and edits,
        <RouterLink :to="{ name: 'UserUploadCampaigns', params: { pageId: customerId } }">click here</RouterLink>.
      </template>
    </NoteWithIcon>

    <!-- No Content messages -->
    <NoteWithIcon v-if="groupingType === 'community-upload' && !dataFromServer?.communityUploadPhotoSummaries?.length" style="margin: 18px 0" color="blue">
      <template #icon><IconEmbedded name="info-simple_4" /></template>
      <template #text>
        <div>
          <h5 class="global-h5">Where are my Community Uploads?</h5>
          <div>Community Upload content will show here as community members upload it.</div>
          <SrpButton size="small" tag="RouterLink" :to="{ name: 'UserUploadCampaigns', params: { pageId: customerId } }" style="margin-top: 10px">View my Upload Links</SrpButton>
        </div>
      </template>
    </NoteWithIcon>

    <div v-else-if="photosToShow.length === 0 && groupingType !== 'my-uploads'">No photos or videos found.</div>
    <!-- / No Content messages -->

    <div v-if="groupingType === MyUploadsSource" class="all-photos-with-grouping__create-group-row">
      <ShowMoreBigButton class="all-photos-with-grouping__create-group-button" @click="() => (isEditPhotosGroupTitleAndCommentModalVisible = true)">
        Create a New Group<IconEmbedded name="plus_3" :size="20" color="rgba(120, 120, 120, 1)" style="position: relative; inset: 4px auto auto 3px" />
      </ShowMoreBigButton>

      <ShowMoreBigButton v-if="showBulkUploadButton" class="all-photos-with-grouping__bulk-upload-button" @click="goToBulkUpload">
        <span class="all-photos-with-grouping__bulk-upload-label">
          Bulk Upload<IconEmbedded name="upload_3" :size="18" color="rgba(120, 120, 120, 1)" class="all-photos-with-grouping__bulk-upload-icon" />
        </span>
      </ShowMoreBigButton>
    </div>

    <EditTitleAndCommentModal
      v-if="isEditPhotosGroupTitleAndCommentModalVisible"
      v-model:isVisible="isEditPhotosGroupTitleAndCommentModalVisible"
      :customerId="customerId"
      @save="
        upsertedPhotoGallery => {
          dataFromServer.customerGalleryPhotoGroups.push(upsertedPhotoGallery);
          groupData();
          $emit('galleryGroupsUpdated', dataFromServer.customerGalleryPhotoGroups);
        }
      "
    />

    <div class="all-photos-with-grouping__photos-spoilers-list-wrap">
      <ul v-if="photosToShow.length > 0" class="all-photos-with-grouping__photos-spoilers-list" style="margin-bottom: 30px">
        <!-- Note: The :key MUST be unique to each group (since the components get reused and we don't want that while the user is uploading)-->
        <PhotosSpoiler
          class="all-photos-with-grouping__photos-spoiler"
          v-for="(group, i) in photosToShow"
          :index="i"
          :key="group.myUploadsGroupId || group.creatorVisitId || group.uploadCampaignId || group.locationId || `photoGroup-${i}`"
          :title="group.groupName"
          :date="group.sortValueAsc ? formatTimeAgo(sortDescending ? group.sortValueDesc : group.sortValueAsc) : ''"
          :email="''"
          :additionalInfo="group.additionalInfo"
          :nonLinkText="group.nonLinkText"
          :linkRoute="group.linkRoute"
          :photosList="[...new Set(group.photos)]"
          :isThumbnailsWithDownloadButton="isThumbnailsWithDownloadButton"
          :isThumbnailsWithCheckboxes="isThumbnailsWithCheckboxes"
          @downloadImage="fileName => downloadFile(fileName)"
          :checkedFileIds="
            localCheckedFiles.filter(p => group.photos.includes(p)) // PhotosSpoiler only needs the info about it's own checked files, not all checked files from all groups
          "
          @checkUncheckFile="handleCheckUncheckFile"
          @checkUncheckAll="isChecked => handleCheckUncheckAll(isChecked, group.photos)"
          :isThumbnailsEntireAreaClickable="true"
          :isThumbnailsEntireAreaCheckable="true"
          :isWithSelectAllCheckbox="isThumbnailsWithCheckboxes || (group.source === MyUploadsSource && isDeleteOrMoveMode)"
          :isMyUploads="group.source === MyUploadsSource"
          :groupingType="groupingType"
          :subGroupingTypes="subGroupingTypes"
          :locationShortPhotoSummariesList="group.locationShortPhotoSummariesList"
          :adventureShortPhotoSummariesList="group.adventureShortPhotoSummariesList"
          :uploadCampaignShortPhotoSummariesList="group.uploadCampaignShortPhotoSummariesList"
          :creatorShortPhotoSummariesList="group.creatorShortPhotoSummariesList"
          :searchText="filterText"
          :isDeleteGroupButtonVisible="isDeleteOrMoveMode"
          :isSelectThumbnailsMode="isDeleteOrMoveMode"
          @toggleDeleteOrMoveMode="isDeleteOrMoveMode = !isDeleteOrMoveMode"
          :myUploadsGroupId="group.myUploadsGroupId"
          :customerId="customerId"
          :currentOperationSource="currentOperationSource"
          :isVideoUploadAllowed="isVideoUploadAllowed"
          @assetUploaded="
            assetId => {
              group.photos.unshift(assetId);
              dataFromServer.customerGalleryPhotoGroups.find(g => g.id === group.myUploadsGroupId).photoIds = [...group.photos];
              $emit('galleryGroupsUpdated', dataFromServer.customerGalleryPhotoGroups);
            }
          "
          :saveFunction="async assetIds => await uploadToCertainGallery(group.myUploadsGroupId, assetIds)"
          @update:titleAndComment="
            upsertedPhotoGallery => {
              group.groupName = upsertedPhotoGallery.groupName;
              group.additionalInfo = upsertedPhotoGallery.comment;
              group.nonLinkText = upsertedPhotoGallery.photoCredit ? `(Photo Credit: ${upsertedPhotoGallery.photoCredit})` : null;
              group.photoCredit = upsertedPhotoGallery.photoCredit;
              group.assignedLocationId = upsertedPhotoGallery.locationId;
              group.googlePlaceId = upsertedPhotoGallery.googlePlaceId;
              group.locationName = upsertedPhotoGallery.locationName;
              const rawGroup = dataFromServer.customerGalleryPhotoGroups.find(g => g.id === group.myUploadsGroupId);
              if (rawGroup) {
                rawGroup.groupName = upsertedPhotoGallery.groupName;
                rawGroup.comment = upsertedPhotoGallery.comment;
                rawGroup.photoCredit = upsertedPhotoGallery.photoCredit;
                rawGroup.locationId = upsertedPhotoGallery.locationId;
                rawGroup.googlePlaceId = upsertedPhotoGallery.googlePlaceId;
                rawGroup.locationName = upsertedPhotoGallery.locationName;
              }
              $emit('galleryGroupsUpdated', dataFromServer.customerGalleryPhotoGroups);
            }
          "
          @deleteGroup="
            () => {
              dataFromServer.customerGalleryPhotoGroups = dataFromServer.customerGalleryPhotoGroups.filter(g => g.id !== group.myUploadsGroupId);
              groupData();
              $emit('galleryGroupsUpdated', dataFromServer.customerGalleryPhotoGroups);
            }
          "
          @uploadStateChanged="handleUploadStateChanged"
          :creatorVisitId="group.creatorVisitId"
          :uploadCampaignId="group.uploadCampaignId"
          :locationId="group.locationId"
          @showAISummary="contentSummary => $emit('showAISummary', contentSummary)"
          :contentSummaryData="contentSummaryData"
          :assignedLocationId="group.assignedLocationId"
          :googlePlaceId="group.googlePlaceId"
          :locationName="group.locationName"
          :photoCredit="group.photoCredit"
        />
      </ul>

      <div v-if="hasMoreGroupsToShow" ref="loadMoreSentinel" class="loading-more-sentinel">
        <h2 class="global-h2">Loading more...</h2>
      </div>

      <SelectedFilesPanel
        v-if="isDeleteOrMoveMode && localCheckedFiles.length"
        class="all-photos-with-grouping__selected-files-panel"
        :selectedFiles="
          localCheckedFiles.map(f => ({
            name: f,
            size: null,
            pending: false,
            active: false,
            serverId: f,
            uploadFileName: null,
            finishedUploading: true,
            blob: null,
            type: '',
          }))
        "
        :isPreviewLinkVisible="false"
        :isDragAndDropOn="false"
        @remove="fileServerId => (localCheckedFiles = localCheckedFiles.filter(f => f !== fileServerId))"
      >
        <template #headerRightSide>
          <LinkWithIcon isDottedUnderline color="teal" iconInset="auto auto auto 2px" @click="cancelDeleteOrMoveMode">
            <template #icon><IconEmbedded name="remove_3-5" :size="12" /></template>
            Cancel
          </LinkWithIcon>

          <LinkWithIcon isDottedUnderline color="red" iconInset="auto auto auto 2px" @click="isDeletePhotosConfirmationModalVisible = true">
            <template #icon><IconEmbedded name="trashcan-alt_2-5" :size="17" /></template>
            {{ isScreenEqualOrSmaller(screenSize, "tablet") ? "Delete" : "Delete selected" }}
          </LinkWithIcon>

          <LinkWithIcon isDottedUnderline color="teal" iconInset="auto auto auto 2px" @click="isMovePhotosToAGalleryModalVisible = true">
            <template #icon><IconEmbedded name="arrow-corner-top-right_2-5" :size="16" /></template>
            {{ isScreenEqualOrSmaller(screenSize, "tablet") ? "Move" : "Move selected to a group" }}
          </LinkWithIcon>
        </template>
      </SelectedFilesPanel>

      <DeletePhotosConfirmationModal
        v-if="isDeletePhotosConfirmationModalVisible"
        v-model:isVisible="isDeletePhotosConfirmationModalVisible"
        :customerId="customerId"
        :fileIdsToDelete="localCheckedFiles"
        :currentOperationSource="currentOperationSource"
        @deletionCompleted="deleteFilesFromManyGroups"
      />

      <MovePhotosToAGalleryModal
        v-if="isMovePhotosToAGalleryModalVisible"
        v-model:isVisible="isMovePhotosToAGalleryModalVisible"
        :photoIds="localCheckedFiles"
        :customerId="customerId"
        :allGroups="
          photosToShow.map(g => ({
            name: g.groupName,
            id: g.myUploadsGroupId,
          }))
        "
        @completeMoving="
          async () => {
            isDeleteOrMoveMode = false;
            isMovePhotosToAGalleryModalVisible = false;
            localCheckedFiles = [];
            await getData();
          }
        "
      />
    </div>

    <div v-if="groupingType === MyUploadsSource && filterPhotoCount > 0" class="all-photos-with-grouping__create-group-row">
      <ShowMoreBigButton class="all-photos-with-grouping__create-group-button" @click="() => (isEditPhotosGroupTitleAndCommentModalVisible = true)">
        Create a New Group<IconEmbedded name="plus_3" :size="20" color="rgba(120, 120, 120, 1)" style="position: relative; inset: 4px auto auto 3px" />
      </ShowMoreBigButton>

      <ShowMoreBigButton v-if="showBulkUploadButton" class="all-photos-with-grouping__bulk-upload-button" @click="goToBulkUpload">
        <span class="all-photos-with-grouping__bulk-upload-label">
          Bulk Upload<IconEmbedded name="upload_3" :size="18" color="rgba(120, 120, 120, 1)" class="all-photos-with-grouping__bulk-upload-icon" />
        </span>
      </ShowMoreBigButton>
    </div>

    <!-- Navigation warning modal -->
    <SrpModal v-model:isVisible="isNavigationWarningVisible" size="small">
      <template #content>
        <h2 class="global-h2">Upload in progress...</h2>
        <div style="margin-top: 10px">Some uploads may not finish if you leave.</div>
      </template>
      <template #footer="{ closeModal }">
        <SrpButton @click="closeModal">Wait for Upload</SrpButton>
        <SrpButton color="gray" fill="outlined" @click="handleConfirmNavigation"> Leave </SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script lang="ts">
import axios from "axios";
import { debounce } from "debounce";
import { defineComponent, inject, ref, computed } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import FileDownload from "@logic/FileDownload";
import FileUtils from "@logic/FileUtils";
import DateUtils from "@logic/DateUtils";
import generateSrc, { ImageSize } from "@helpers/GenerateGallerySrc";
import { useHead } from "@unhead/vue";
import { includesCaseInsensitive } from "@logic/String";
import { isScreenEqualOrSmaller } from "@helpers/ScreenSizeHelper";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import lodashReverse from "lodash-es/reverse";

// Types
import {
  PhotosPageData,
  SubGroupingType,
  LocationShortPhotoSummary,
  CreatorShortPhotoSummary,
  GroupingType,
  UploadCampaignShortPhotoSummary,
  AdventureShortPhotoSummary,
  PhotosPageDataFromServer,
  PhotoGallerySource,
} from "@contracts/photosPageData";
import { ScreenSize } from "@contracts/screenSize";

// Components
import CopyText from "@components/CopyText.vue";
import Loader from "@components/Loader/Loader.vue";
import VideoRender from "@components/VideoRender.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import PhotosSpoiler from "./PhotosSpoiler.vue";
import SearchAndFilters from "./SearchAndFilters.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import TabsSection from "./TabsSection.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import MovePhotosToAGalleryModal from "./MovePhotosToAGalleryModal.vue";
import DeletePhotosConfirmationModal from "./DeletePhotosConfirmationModal.vue";
import OrgContext from "@/logic/OrgContext";
import ShowMoreBigButton from "@components/ui/ShowMoreBigButton.vue";
import EditTitleAndCommentModal from "./EditTitleAndCommentModal.vue";
import { ContentSummaryData } from "@/contracts/contentCalendar";
import SrpModal from "@components/ui/SrpModal.vue";
import { BULK_UPLOAD_RETURN_TO } from "@views/Community/BulkAssetUpload/bulkUploadReturnTo";

export default defineComponent({
  name: "AllPhotosSection",

  components: {
    ShowMoreBigButton,
    DeletePhotosConfirmationModal,
    MovePhotosToAGalleryModal,
    SelectedFilesPanel,
    SrpButton,
    IconEmbedded,
    CopyText,
    NoteWithIcon,
    LinkWithIcon,
    Loader,
    VideoRender,
    PhotosSpoiler,
    SearchAndFilters,
    TabsSection,
    EditTitleAndCommentModal,
    SrpModal,
  },

  props: {
    customerId: { type: String, required: true },
    checkedFiles: { type: Array as () => Array<string>, default: () => [], required: false },
    isThumbnailsWithCheckboxes: { type: Boolean, default: false, required: false },
    isThumbnailsWithDownloadButton: { type: Boolean, default: false, required: false },
    showTotalAssetCount: { type: Boolean, default: true, required: false },
    contentSummaryData: { type: Array as () => Array<ContentSummaryData>, default: () => [], required: false },
    currentOperationSource: { type: String as () => PhotoGallerySource | null, default: null, required: false },
    onlyShowCustomerUploads: { type: Boolean, default: false, required: false },
    isVideoUploadAllowed: { type: Boolean, default: true, required: false },
    isDebug: { type: Boolean, default: false, required: false },
    showBulkUploadButton: { type: Boolean, default: true, required: false },
    showCommunityUploadLinksNote: { type: Boolean, default: false, required: false },
  },

  emits: {
    checkUncheckFile: (_fileName: string, _isChecked: boolean) => true,
    checkUncheckAll: (_images: Array<string>, _isChecked: boolean) => true,
    dataLoaded: (_data: any) => true,
    showAISummary: (_contentSummary: ContentSummaryData) => true,
    galleryGroupsUpdated: (_galleryGroups: any) => true,
    anyUploadingChanged: (_isAnyUploading: boolean) => true,
  },

  setup() {
    // Navigation warning state (needs to be in setup for onBeforeRouteLeave to work)
    const isNavigationWarningVisible = ref(false);
    const pendingNavigation = ref<any>(null);
    const pendingNavigationType = ref<"route" | "tab" | null>(null);
    const uploadingGroupIds = ref(new Set<string>());
    const skipRouteGuard = ref(false); // Flag to bypass guard after user confirms

    const isAnyPending = computed(() => {
      return uploadingGroupIds.value.size > 0;
    });

    // Register route leave guard - this works in child components with Composition API
    onBeforeRouteLeave(to => {
      // Allow navigation if user already confirmed
      if (skipRouteGuard.value) {
        skipRouteGuard.value = false; // Reset for next time
        return true;
      }
      if (isAnyPending.value) {
        pendingNavigation.value = to;
        pendingNavigationType.value = "route";
        isNavigationWarningVisible.value = true;
        return false;
      }
      return true;
    });

    return {
      isNavigationWarningVisible,
      pendingNavigation,
      pendingNavigationType,
      uploadingGroupIds,
      isAnyPending,
      skipRouteGuard,
    };
  },

  data() {
    return {
      globalLog: inject("globalLog") as any,
      screenSize: inject("screenSize") as ScreenSize,

      title: "Photos & Videos",
      isLoading: true,
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,
      maxResults: 50,
      downloadUncompressed: true,

      // Data
      dataFromServer: null as PhotosPageDataFromServer | null, // Raw data from server (we structure it differently on this page)
      groupedPhotoData: Array<PhotosPageData>(), // Grouped but not filtered
      filteredPhotoData: Array<PhotosPageData>(), // Filters applied to this
      photosToShow: Array<PhotosPageData>(), // Lazy loading applied ot this
      filterPhotoCount: 0,
      groupResultCount: 0,
      debounceCallback: null,
      sortField: "date",
      sortDescending: true,

      // Filtering fields
      debounceWaitInMs: 400,
      filterText: "",
      filterShowVideos: true,
      filterShowPhotos: true,
      groupingType: "adventure" as GroupingType,

      isUserCommentsTextExpanded: [],

      MyUploadsSource: "my-uploads",

      isDeleteOrMoveMode: false,

      localCheckedFiles: [] as Array<string>,

      isMovePhotosToAGalleryModalVisible: false,
      isDeletePhotosConfirmationModalVisible: false,

      photoIdsCollector: [] as Array<string>,

      isEditPhotosGroupTitleAndCommentModalVisible: false,

      // Lazy loading state
      // How many groups to show by default and add for each pagination
      GROUPS_PER_PAGE: 12,
      visibleGroupCount: 12, // Will be set to GROUPS_PER_PAGE in mounted
      isLoadingMore: false,
      loadMoreObserver: null as IntersectionObserver | null,
    };
  },

  computed: {
    subGroupingTypes(): Array<SubGroupingType> {
      return (
        {
          adventure: ["byLocation" /* , "byHighlights" */],
          creator: ["byAdventure", "byUploadCampaign"],
          location: ["byCreator"],
          "community-upload": ["byCreator"],
        } as Record<GroupingType, Array<SubGroupingType>>
      )[this.groupingType];
    },
    sortFieldText(): string {
      if (this.sortField === "date") {
        return this.sortDescending ? "newest" : "oldest";
      } else return this.sortDescending ? "name desc" : "name asc";
    },
    groupTypeTextPluralized(): string {
      let name: string = this.groupingType;
      if (this.groupingType === "community-upload") name = "community upload";
      if (this.groupingType === "adventure") name = "creator visit";
      if (this.groupingType === "my-uploads") name = "group";
      return name + (this.groupResultCount === 1 ? "" : "s");
    },
    debugText(): string {
      return this.isAnyPending ? "UPLOADING" : "Not Uploading";
    },
    debugStyles(): Record<string, string> {
      return {
        color: this.isAnyPending ? "red" : "black",
        fontWeight: this.isAnyPending ? "bold" : "normal",
      };
    },
    hasMoreGroupsToShow(): boolean {
      return this.visibleGroupCount < this.filteredPhotoData.length;
    },
  },

  // Keeps the local copy in sync with the checkedFiles prop
  watch: {
    checkedFiles: {
      immediate: true,
      deep: true,
      handler(newFiles: string[]) {
        this.localCheckedFiles = [...(newFiles ?? [])];
      },
    },
  },

  async mounted() {
    useHead({ title: () => this.title ?? "" });

    // Initialize visible count from constant
    this.visibleGroupCount = this.GROUPS_PER_PAGE;

    var defaultedGroupingType = false;
    if (this.onlyShowCustomerUploads) {
      this.groupingType = "my-uploads";
      defaultedGroupingType = true;
    } else {
      defaultedGroupingType = this.checkForDefaultFilters();
    }

    await this.getData();
    this.updateHash();

    if (!this.onlyShowCustomerUploads && !defaultedGroupingType) {
      this.checkForDefaultTab();
    }

    const sectionIdHashParam = this.getHashParameter("sectionId");
    this.scrollToACertainSection(sectionIdHashParam);

    // Add beforeunload listener to warn about pending uploads
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    // Set up Intersection Observer for infinite scroll after data loads
    this.$nextTick(() => {
      this.setupIntersectionObserver();
    });
  },

  beforeUnmount() {
    // Clean up beforeunload listener
    window.removeEventListener("beforeunload", this.handleBeforeUnload);

    // Clean up Intersection Observer
    if (this.loadMoreObserver) {
      this.loadMoreObserver.disconnect();
      this.loadMoreObserver = null;
    }
  },

  methods: {
    formatTimeAgo: DateUtils.formatTimeAgo,
    includesCaseInsensitive,
    isScreenEqualOrSmaller,
    lodashReverse,
    cancelDeleteOrMoveMode() {
      this.isDeleteOrMoveMode = false;
      this.localCheckedFiles = [];
    },
    goToBulkUpload() {
      const returnTo = this.$route.name === "CommunityPhotos" ? BULK_UPLOAD_RETURN_TO.library : BULK_UPLOAD_RETURN_TO.atlas;
      this.$router.push({ name: "BulkAssetUploadPage", params: { customerId: this.customerId }, query: { returnTo } });
    },
    async deleteFilesFromManyGroups() {
      this.isDeleteOrMoveMode = false;
      this.isDeletePhotosConfirmationModalVisible = false;
      this.dataFromServer.customerGalleryPhotoGroups = this.dataFromServer.customerGalleryPhotoGroups.map(group => {
        group.photoIds = group.photoIds.filter(photoId => !this.localCheckedFiles.includes(photoId));
        return group;
      });
      this.localCheckedFiles = [];
      this.groupData();
      this.$emit("galleryGroupsUpdated", this.dataFromServer.customerGalleryPhotoGroups);
    },
    uploadImageToUndefinedGallery(photoServerId: string) {
      this.photoIdsCollector.push(photoServerId);
    },
    async uploadToCertainGallery(galleryId: string, imageIds: Array<string>) {
      await PhotoGalleryV2Repo.addPhotosToGroup(this.customerId, galleryId, imageIds, this.currentOperationSource);
    },
    handleCheckUncheckAll(isChecked: boolean, groupPhotos: Array<string>) {
      this.$emit("checkUncheckAll", [...new Set(groupPhotos)], isChecked);

      if (isChecked) {
        this.localCheckedFiles = [...new Set(this.localCheckedFiles.concat(groupPhotos))];
      } else {
        this.localCheckedFiles = this.localCheckedFiles.filter(f => !groupPhotos.includes(f));
      }
    },
    handleCheckUncheckFile(fileName: string, isChecked: boolean) {
      this.$emit("checkUncheckFile", fileName, isChecked);

      if (isChecked) {
        this.localCheckedFiles.push(fileName);
      } else {
        this.localCheckedFiles = this.localCheckedFiles.filter(f => f !== fileName);
      }
    },
    handleUploadStateChanged({ groupId, isUploading }: { groupId: string; isUploading: boolean }) {
      const wasAnyUploading = this.uploadingGroupIds.size > 0;
      if (isUploading) {
        this.uploadingGroupIds.add(groupId);
      } else {
        this.uploadingGroupIds.delete(groupId);
      }
      const isAnyUploading = this.uploadingGroupIds.size > 0;
      if (wasAnyUploading !== isAnyUploading) {
        this.$emit("anyUploadingChanged", isAnyUploading);
      }
    },
    handleConfirmNavigation() {
      this.isNavigationWarningVisible = false;
      if (this.pendingNavigationType === "route" && this.pendingNavigation) {
        // Set flag to skip the guard on re-navigation
        this.skipRouteGuard = true;
        this.$router.push(this.pendingNavigation);
      } else if (this.pendingNavigationType === "tab" && this.pendingNavigation) {
        // For tab switching, we need to bypass the check in setGroupingType
        const targetTab = this.pendingNavigation;
        this.pendingNavigation = null;
        this.pendingNavigationType = null;
        // Directly change the grouping without the pending check
        this.filterText = "";
        this.groupingType = targetTab;
        this.updateHash();
        this.groupData();
        return;
      }
      this.pendingNavigation = null;
      this.pendingNavigationType = null;
    },
    async getData() {
      this.isLoading = true;
      const url = `${import.meta.env.VITE_API_URL}/photos/${this.customerId}/v2`;
      const { data } = await axios.get(url);
      this.dataFromServer = data;
      this.$emit("dataLoaded", data);
      this.groupData();
      this.isLoading = false;
      this.title += ` - ${this.dataFromServer.communityTitle}`;
    },
    groupData() {
      var startTime = performance.now();
      let grouped = new Array<PhotosPageData>();
      if (this.groupingType === "adventure") {
        // Adventure Photos
        this.dataFromServer.adventurePhotoSummaries.forEach(adv => {
          let photoCredit = adv.creatorDisplayName;
          if (adv.creatorCredit?.length > 0) photoCredit = adv.creatorCredit;
          let group = this.createPhotoGroup(
            adv.adventureName,
            [],
            // New logic allows group by collab
            adv.isCollab
              ? { name: "PaidCollabCommunityAdmin", params: { communityId: this.customerId }, hash: `#collab=${adv.adventureId}` }
              : { name: "ItineraryPreview", params: { itineraryId: adv.uniqueName } },
            "adventure",
            adv.publishedDate,
            adv.publishedDate,
            `(Photo Credit: ${photoCredit})`,
            null,
            null,
            null,
            null,
            null,
            null
          );

          group.creatorVisitId = adv.adventureId;

          // Add the photos (how varies a bit if this is a collab or adventure)
          if (adv.titlePhoto) group.photos.push(adv.titlePhoto);
          if (adv.collabPhotos?.length > 0) group.photos.push(...adv.collabPhotos);
          else adv.locations.forEach(loc => group.photos.push(...loc.photos));

          // Add the subGrouping data
          // "my-uploads" group doesn't have any subgroups
          if (this.groupingType !== "my-uploads") {
            adv.locations.forEach(location => {
              if (!Array.isArray(group.locationShortPhotoSummariesList)) {
                group.locationShortPhotoSummariesList = [];
              }
              group.locationShortPhotoSummariesList.push({
                locationId: location.locationId,
                locationName: location.locationName,
                photos: location.photos,
              } as LocationShortPhotoSummary);
            });
          }

          grouped.push(group);
        });
      } else if (this.groupingType === "location") {
        // Adventure Photos by Location
        // Previously used flatMap but wanted a parent field so ended up with this
        const flattenedByLocation = [];

        // Creates a map of all photoId's to be able to find their creators easily later
        const photosAndItsCreators: Map<string, { creatorId: string; creatorName: string }> = new Map();
        this.dataFromServer.adventurePhotoSummaries.forEach(advevnture => {
          advevnture.locations.forEach(location => {
            location.photos.forEach(photoId => {
              photosAndItsCreators.set(photoId, { creatorId: advevnture.creatorId, creatorName: advevnture.creatorDisplayName });
            });
          });
        });

        this.dataFromServer.adventurePhotoSummaries.forEach(adv => {
          adv.locations.forEach(loc => {
            flattenedByLocation.push({
              publishedDate: adv.publishedDate,
              location: loc,
            });
          });
        });
        // const flattenedByLocation = this.dataFromServer.adventurePhotoSummaries.flatMap(adv => adv.locations);
        const locationGrouping = this.groupBy(flattenedByLocation, loc => loc.location.locationId);
        locationGrouping.forEach(locationGroup => {
          let sortMinMax = this.getMinMax(locationGroup, loc => loc.publishedDate);
          let group = this.createPhotoGroup(locationGroup[0].location.locationName, [], null, "adventure", sortMinMax[0], sortMinMax[1], null, null, null, null, null, null, null);
          group.locationId = locationGroup[0].location.locationId;
          if (!group.groupName || group.groupName.length === 0) group.groupName = "Unknown Location";
          locationGroup.forEach(loc => {
            group.photos.push(...loc.location.photos);
          });
          // Exclude locations without any photos
          if (group.photos.length > 0) {
            // Add the subGrouping data
            group.photos.forEach(photoId => {
              if (!Array.isArray(group.creatorShortPhotoSummariesList)) {
                group.creatorShortPhotoSummariesList = [];
              }

              // Find the target Short Photo Summary
              const targetShortPhotoSummary = group.creatorShortPhotoSummariesList.find(s => {
                return photosAndItsCreators.has(photoId) && s.creatorId === photosAndItsCreators.get(photoId)?.creatorId;
              });

              if (targetShortPhotoSummary) {
                targetShortPhotoSummary.photos.push(photoId);
              } else {
                group.creatorShortPhotoSummariesList.push({
                  creatorId: photosAndItsCreators.get(photoId)?.creatorId,
                  creatorName: photosAndItsCreators.get(photoId)?.creatorName,
                  photos: [photoId],
                } as CreatorShortPhotoSummary);
              }
            });

            grouped.push(group);
          }
        });
        // NOTE: Not showing Community Uploads and My Uploads by location (since we don't have that data)
      } else if (this.groupingType === "community-upload") {
        // Only applies to Community Uploads
        const campaignIdGrouping = this.groupBy(this.dataFromServer.communityUploadPhotoSummaries, upload => upload.campaignId);
        campaignIdGrouping.forEach(byCampaignGroup => {
          let sortMinMax = this.getMinMax(byCampaignGroup, loc => loc.createdDate);
          let creatorNames = byCampaignGroup.map(upload => `${upload.uploadersName} (${upload.uploadersEmail})`).join(", ");
          let group = this.createPhotoGroup(
            byCampaignGroup[0].campaignName,
            [],
            null,
            "community-upload",
            sortMinMax[0],
            sortMinMax[1],
            null,
            "Uploads by " + creatorNames,
            null,
            null,
            null,
            null,
            null
          );
          if (!group.groupName || group.groupName.length === 0) {
            group.groupName = "Unknown Campaign";
          }

          // Add the subGrouping data
          byCampaignGroup.forEach(campaign => {
            if (!Array.isArray(group.creatorShortPhotoSummariesList)) {
              group.creatorShortPhotoSummariesList = [];
            }
            group.creatorShortPhotoSummariesList.push({
              creatorId: campaign.uploadersEmail,
              creatorName: `${campaign.uploadersName} (${campaign.uploadersEmail})`,
              photos: campaign.photos,
              additionalInfo: campaign.additionalInfo,
            } as CreatorShortPhotoSummary);

            group.uploadCampaignId = campaign.campaignId;
          });

          byCampaignGroup.forEach(upload => {
            group.photos.push(...upload.photos);
          });
          grouped.push(group);
        });
      } else if (this.groupingType === this.MyUploadsSource) {
        // My Uploads
        this.dataFromServer.customerGalleryPhotoGroups.forEach(rawPhotoGroup => {
          const photoCreditText = rawPhotoGroup.photoCredit ? `(Photo Credit: ${rawPhotoGroup.photoCredit})` : null;
          let group = this.createPhotoGroup(
            rawPhotoGroup.groupName || "My Uploads",
            [...rawPhotoGroup.photoIds],
            null,
            this.MyUploadsSource,
            rawPhotoGroup.createdOn,
            rawPhotoGroup.createdOn,
            photoCreditText,
            rawPhotoGroup.comment,
            null,
            null,
            null,
            null,
            rawPhotoGroup.id,
            rawPhotoGroup.googlePlaceId,
            rawPhotoGroup.locationName,
            rawPhotoGroup.photoCredit
          );
          group.assignedLocationId = rawPhotoGroup.locationId;
          grouped.push(group);
        });
      }
      var endTime = performance.now();
      this.globalLog.info(`Grouping took ${endTime - startTime}ms`);

      this.groupedPhotoData = grouped;
      // Reset visible count when grouping changes
      this.visibleGroupCount = this.GROUPS_PER_PAGE;
      this.applyFilters();
    },
    getMinMax(inputArray, fieldGetter) {
      if (!inputArray) {
        return null;
      }
      let minV = fieldGetter(inputArray[0]);
      let maxV = fieldGetter(inputArray[0]);
      for (let row of inputArray) {
        let rowVal = fieldGetter(row);
        if (rowVal) {
          if (rowVal < minV) minV = rowVal;
          if (rowVal > maxV) maxV = rowVal;
        }
      }
      return [minV, maxV];
    },
    calculateCounts() {
      this.groupResultCount = this.filteredPhotoData.length;
      this.filterPhotoCount = this.filteredPhotoData.reduce((n, { photos }) => n + photos.length, 0);
    },
    searchChanged() {
      // Note: Using Debounce here so it only actually fires periodically
      // this.globalLog.info("Search changed " + this.filterText);
      if (this.debounceCallback) {
        this.debounceCallback();
      } else {
        this.debounceCallback = debounce(this.applyFilters, this.debounceWaitInMs);
        this.debounceCallback();
      }
    },
    applyFilters() {
      this.globalLog.info("ApplyFilter " + this.filterText);

      var startTime = performance.now();
      // this.filterInProgress = true;
      let filterMatches = [];

      // Cache the lowercase search term once (avoid repeated toLowerCase calls)
      const searchTermLower = this.filterText?.trim().toLowerCase() || "";

      this.groupedPhotoData.forEach(inputGroup => {
        // Check if the group passes filters
        if (this.doesGroupPassFilters(inputGroup, searchTermLower)) {
          // Check which photos pass filters
          let groupToAdd = this.applyPhotoFilter(inputGroup);
          // Show empty groups for My Uploads
          var showEmptyGroup = groupToAdd.source === this.MyUploadsSource;
          if (groupToAdd?.photos.length > 0 || showEmptyGroup) {
            filterMatches.push(groupToAdd);
          }
        }
      });
      this.filteredPhotoData = filterMatches;
      var endTime = performance.now();
      // this.filterInProgress = false;
      this.globalLog.info(`Filtering took ${endTime - startTime}ms`);

      // Sort
      this.sortFilteredData();

      // Reset visible count when filters change
      this.visibleGroupCount = this.GROUPS_PER_PAGE;
      this.calculateCounts();
      this.buildListToShow();
      this.updateHash();

      // Re-setup observer after filters change (sentinel might appear/disappear)
      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    },
    sortFilteredData() {
      if (this.sortField === "name") {
        if (this.sortDescending) this.filteredPhotoData.sort((a, b) => -a.groupName.localeCompare(b.groupName));
        else this.filteredPhotoData.sort((a, b) => a.groupName.localeCompare(b.groupName));
      } else {
        // Sorting by Date
        // The null coalesce should push null dates to the end
        if (this.sortDescending) this.filteredPhotoData.sort((a, b) => -(a.sortValueDesc ?? "2000").localeCompare(b.sortValueDesc ?? "2000"));
        else this.filteredPhotoData.sort((a, b) => (a.sortValueAsc ?? "9999").localeCompare(b.sortValueAsc ?? "9999"));
      }
    },
    searchInsideSubgroups(
      subGroupsList: Array<LocationShortPhotoSummary> | Array<CreatorShortPhotoSummary>,
      subGroupingType: SubGroupingType,
      searchTermLower: string,
      inputGroup: PhotosPageData
    ): boolean {
      if (!subGroupsList?.length) return false;

      const targetField = { byLocation: "locationName", byCreator: "creatorName" }[subGroupingType];

      // Convert to Set for O(1) lookup instead of O(n) .includes()
      const photosSet = new Set(inputGroup.photos);

      // Use .some() for early termination instead of .map().filter().length
      return subGroupsList.some(subGroup => {
        // Use pre-cached lowercase search term
        const isTargetFieldMatches = subGroup[targetField]?.toLowerCase().includes(searchTermLower) === true;
        let isAdditionalInfoMatches = false;

        if (this.groupingType === "community-upload" && subGroup.additionalInfo) {
          isAdditionalInfoMatches = includesCaseInsensitive(subGroup.additionalInfo, searchTermLower);
        }

        if (!isTargetFieldMatches && !isAdditionalInfoMatches) return false;

        // Check if any photos from this subgroup exist in the input group (O(1) per check now)
        return subGroup.photos?.some(p => photosSet.has(p)) === true;
      });
    },
    doesGroupPassFilters(inputGroup: PhotosPageData, searchTermLower: string): boolean {
      // Source filters FIRST - quick check to skip expensive text search for other tabs
      if (this.groupingType === "adventure" && inputGroup.source !== "adventure") return false;
      if (this.groupingType === "location" && inputGroup.source !== "adventure") return false;
      if (this.groupingType === "community-upload" && inputGroup.source !== "community-upload") return false;
      if (this.groupingType === this.MyUploadsSource && inputGroup.source !== this.MyUploadsSource) return false;

      // Text search (only if there's a search term)
      if (searchTermLower.length > 0) {
        // Use pre-cached lowercase search term for comparisons
        const isGroupNameHasMatches = inputGroup.groupName?.toLowerCase().includes(searchTermLower) === true;

        // Short-circuit: if group name matches, skip expensive subgroup search
        if (isGroupNameHasMatches) return true;

        let isSubGroupsHasMatches = false;
        let isMyUploadsExtraMatches = false;

        if (this.groupingType === "adventure") {
          isSubGroupsHasMatches = this.searchInsideSubgroups(inputGroup.locationShortPhotoSummariesList, "byLocation", searchTermLower, inputGroup);
        } else if (this.groupingType === "location") {
          isSubGroupsHasMatches = this.searchInsideSubgroups(inputGroup.creatorShortPhotoSummariesList, "byCreator", searchTermLower, inputGroup);
        } else if (this.groupingType === "community-upload") {
          isSubGroupsHasMatches = this.searchInsideSubgroups(inputGroup.creatorShortPhotoSummariesList, "byCreator", searchTermLower, inputGroup);
        } else if (this.groupingType === this.MyUploadsSource) {
          isMyUploadsExtraMatches = inputGroup.additionalInfo?.toLowerCase().includes(searchTermLower) === true || inputGroup.locationName?.toLowerCase().includes(searchTermLower) === true;
        }

        if (!isSubGroupsHasMatches && !isMyUploadsExtraMatches) return false;
      }

      return true;
    },
    applyPhotoFilter(inputGroup: PhotosPageData): PhotosPageData {
      // Currently there's only a single photo-level filter.
      // If this is show all just return the input
      if (this.filterShowVideos === true && this.filterShowPhotos === true) {
        return inputGroup;
      }
      let groupToReturn = this.createPhotoGroup(
        inputGroup.groupName,
        [],
        inputGroup.linkRoute,
        inputGroup.source,
        inputGroup.sortValueAsc,
        inputGroup.sortValueDesc,
        inputGroup.nonLinkText,
        inputGroup.additionalInfo,
        inputGroup.creatorShortPhotoSummariesList,
        inputGroup.locationShortPhotoSummariesList,
        inputGroup.uploadCampaignShortPhotoSummariesList,
        inputGroup.adventureShortPhotoSummariesList,
        inputGroup.myUploadsGroupId,
        inputGroup.googlePlaceId,
        inputGroup.locationName,
        inputGroup.photoCredit
      );
      groupToReturn.assignedLocationId = inputGroup.assignedLocationId;
      groupToReturn.locationId = inputGroup.locationId;
      groupToReturn.creatorVisitId = inputGroup.creatorVisitId;
      groupToReturn.uploadCampaignId = inputGroup.uploadCampaignId;
      inputGroup.photos.forEach(photo => {
        // Video or Photo check
        let isVideo = this.isVideo(photo);
        if (this.filterShowVideos && isVideo) groupToReturn.photos.push(photo);
        else if (this.filterShowPhotos && !isVideo) groupToReturn.photos.push(photo);
      });
      return groupToReturn;
    },
    // ---Pagination---
    // This made a big difference for large libraries (reduces the sheer number of DOM elements rendered)
    // We could tweak this or likely further improve by using virtual scrolling.
    buildListToShow() {
      // Slice filtered data based on visibleGroupCount for lazy loading
      this.photosToShow = this.filteredPhotoData.slice(0, this.visibleGroupCount);
      this.globalLog.info(`buildListToShow: showing ${this.photosToShow.length} of ${this.filteredPhotoData.length} groups`);
    },
    loadMoreGroups() {
      if (this.visibleGroupCount >= this.filteredPhotoData.length) return;

      this.isLoadingMore = true;
      this.visibleGroupCount = Math.min(this.visibleGroupCount + this.GROUPS_PER_PAGE, this.filteredPhotoData.length);
      this.buildListToShow();
      this.isLoadingMore = false;

      // Re-setup observer after loading more (sentinel might have moved)
      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    },
    setupIntersectionObserver() {
      // Clean up existing observer
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect();
        this.loadMoreObserver = null;
      }

      const sentinel = this.$refs.loadMoreSentinel as HTMLElement;
      if (sentinel && this.hasMoreGroupsToShow) {
        this.loadMoreObserver = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting && this.hasMoreGroupsToShow && !this.isLoadingMore) {
                this.loadMoreGroups();
              }
            });
          },
          {
            rootMargin: "100px", // Start loading before reaching the bottom
          }
        );
        this.loadMoreObserver.observe(sentinel);
      }
    },
    createPhotoGroup(
      groupName: string,
      photos: Array<string>,
      linkRoute: any | null,
      source: string,
      sortValueAsc: string,
      sortValueDesc: string,
      nonLinkText: string,
      additionalInfo?: string | null,
      creatorShortPhotoSummariesList?: Array<CreatorShortPhotoSummary> | null,
      locationShortPhotoSummariesList?: Array<LocationShortPhotoSummary> | null,
      uploadCampaignShortPhotoSummariesList?: Array<UploadCampaignShortPhotoSummary> | null,
      adventureShortPhotoSummariesList?: Array<AdventureShortPhotoSummary> | null,
      myUploadsGroupId?: string,
      googlePlaceId?: string | null,
      locationName?: string | null,
      photoCredit?: string | null
    ): PhotosPageData {
      return {
        groupName: groupName,
        photos: photos,
        linkRoute: linkRoute,
        // Currently used for PhotoCredit
        nonLinkText: nonLinkText,
        source: source,
        sortValueAsc: sortValueAsc,
        sortValueDesc: sortValueDesc,
        additionalInfo: additionalInfo,
        creatorShortPhotoSummariesList,
        locationShortPhotoSummariesList,
        uploadCampaignShortPhotoSummariesList,
        adventureShortPhotoSummariesList,
        myUploadsGroupId,
        googlePlaceId,
        locationName,
        photoCredit,
      } as PhotosPageData;
    },
    setGroupingType(type: GroupingType) {
      // Check for pending uploads before switching tabs
      if (this.isAnyPending && type !== this.groupingType) {
        this.pendingNavigation = type;
        this.pendingNavigationType = "tab";
        this.isNavigationWarningVisible = true;
        return; // Don't switch yet
      }

      this.filterText = ""; // reset search term on grouping type change
      this.groupingType = type;
      this.updateHash();
      this.groupData();
    },
    handleBeforeUnload(e: BeforeUnloadEvent) {
      if (this.isAnyPending) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
    reverseSort() {
      this.sortDescending = !this.sortDescending;
      this.applyFilters();
    },
    async downloadFile(imageName: string) {
      await FileDownload.downloadFile(imageName, this.downloadUncompressed, this.customerId, "photos-page");
    },
    scrollToACertainSection(sectionIdHashParam: string) {
      if (sectionIdHashParam) {
        // Show all groups first so the target section exists in DOM
        this.visibleGroupCount = this.filteredPhotoData.length;
        this.buildListToShow();

        this.$nextTick(() => {
          // Wait a bit for content to render before scrolling
          setTimeout(() => {
            document.getElementById(sectionIdHashParam)?.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }, 100);
        });
      }
    },
    checkForDefaultFilters(): boolean {
      var defaultedGroupingType = false;
      // Some pages navigate in and want us to auto-apply filters
      let justCommunityUploads = this.getHashParameter("justCommunityUploads");
      if (justCommunityUploads) {
        // They typically get notified because a new creator uploads,
        // so defaulting this to creator (could also deafult to community-upload)
        this.groupingType = "community-upload";
        defaultedGroupingType = true;
      }
      let group = this.getHashParameter("group");
      if (group) {
        this.groupingType = group as GroupingType;
        defaultedGroupingType = true;
      } else {
        // For trial customers just deafult to their own uploads (since that's all they'll have)
        var orgInContext = OrgContext.getOrgInContext(this);
        if (orgInContext?.subType?.startsWith("Trial")) {
          this.groupingType = "my-uploads";
          defaultedGroupingType = true;
        }
      }
      let searchText = this.getHashParameter("search");
      if (searchText) {
        this.filterText = searchText;
      }

      return defaultedGroupingType;
    },
    updateHash() {
      // Keep track of the filters based on the hash
      let hash = new URLSearchParams();
      hash.set("group", this.groupingType);
      if (this.filterText) hash.set("search", this.filterText);

      const sectionIdHashParam = this.getHashParameter("sectionId");
      if (sectionIdHashParam) hash.set("sectionId", sectionIdHashParam);

      // (future) Comma separate filters
      const url = `${window.location.pathname}#${hash.toString()}`;
      history.replaceState(null, "", url);
    },
    getHashParameter(key: string): string | null {
      if (window.location.hash.length < 2) return null;
      const parsedHash = new URLSearchParams(window.location.hash.substring(1));
      return parsedHash.get(key);
    },
    generateImageSrc(imageName: string, size: ImageSize): string {
      return generateSrc(imageName, size);
    },
    isVideo(assetName) {
      return FileUtils.isVideoFileType(assetName, null);
    },
    checkForDefaultTab() {
      if (this.dataFromServer.adventurePhotoSummaries?.length > 0) {
        // No action since this is the default tab
      } else if (this.dataFromServer.communityUploadPhotoSummaries?.length > 0) {
        this.setGroupingType("community-upload");
      } else {
        // Fallback to showing them a way to upload their own photos
        this.setGroupingType("my-uploads");
      }
    },
    // Takes an Array<>, and a grouping property.
    // Returns a Map, grouped by the grouping property.
    groupBy(list, keyGetter) {
      const map = new Map();
      list.forEach(item => {
        let key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
          map.set(key, [item]);
        } else {
          collection.push(item);
        }
      });
      return map;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Debug text =================================================================
.debug-text {
  position: absolute;
  right: 15px;
}

// Sorting selector ===========================================================
.sorting-selector {
  display: flex;
  align-items: center;

  &__title {
    margin-right: 2px;
  }

  &__selector {
  }
}

// Counter and sorting ========================================================
.counter-and-sorting {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__counter-and-reorder-button {
    gap: 12px 30px;
    display: flex;
    align-items: center;
  }

  &__counter {
    color: rgba(91, 91, 91, 1);
    font: 300 14px/18px sans-serif;

    b {
      color: rgba(0, 0, 0, 1);
      font:
        700 14px/18px "Quicksand",
        sans-serif;
    }
  }

  &__reorder-button {
  }

  &__sorting-selector {
    justify-self: flex-end;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .counter-and-sorting {
    gap: 12px 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
}

// All photos with grouping ===================================================
.all-photos-with-grouping {
  max-width: calc(100dvw - 36px);

  &__tabs {
    margin-bottom: 15px;
  }

  &__search-and-filters {
    margin-bottom: 18px;
  }

  &__counter-and-sorting {
    margin-bottom: 20px;
  }

  &__community-upload-links-note {
    margin: 0 0 18px;
  }

  &__create-group-row {
    gap: 10px;
    display: flex;
    align-items: stretch;
    margin: 0 0 20px -25px;
    width: calc(100% + 50px);
  }

  &__create-group-button {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__bulk-upload-button {
    flex: 0 0 auto;
    flex-shrink: 0;
  }

  &__bulk-upload-label {
    margin: 0 40px;
  }

  &__bulk-upload-icon {
    position: relative;
    inset: 3px auto auto 4px;
  }

  &__photos-spoilers-list-wrap {
    position: relative;
  }

  &__photos-spoilers-list {
    gap: 27px;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    list-style: none;
  }

  &__photos-spoiler {
    // Turned this off since it somewhat impacted the deep linking to a specific group (and it didn't have a big benefit)
    // Skip rendering work for off-screen items (performance optimization)
    // content-visibility: auto;
    // contain-intrinsic-size: auto 200px; // estimated collapsed height
  }

  &__selected-files-panel {
    width: calc(100% + 50px);
    margin-left: -25px;
    position: sticky !important;
    inset: auto auto 0 0;
    z-index: 4;
  }
}

// Loading more sentinel ======================================================
.loading-more-sentinel {
  padding: 40px;
  margin-bottom: 20px;
  text-align: center;
  color: rgba(91, 91, 91, 1);
  font: 14px/18px sans-serif;
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .all-photos-with-grouping {
    &__tabs {
      margin: 0 -20px 30px;
    }

    &__counter-and-sorting {
    }

    // Full-bleed row width overflows narrow viewports; keep both buttons in the content box.
    &__create-group-row {
      width: 100%;
      margin: 0 0 20px;
      min-width: 0;
      gap: 8px;
    }

    &__create-group-button {
      flex: 1 1 0;
      min-width: 0;
      flex-wrap: wrap;

      :deep(.show-more-btn__text) {
        overflow-wrap: break-word;
        text-align: center;
      }
    }

    &__bulk-upload-label {
      margin: 0 15px;
    }
  }
}
</style>
